import { useProjectIntakeSubmitContext } from "@/components";
import { useDynamicFormContext } from "@/components/DynamicForm/DynamicFormProvider";
import { ValidationMessages } from "@/components/Layout";
import { StickyActionsHeader } from "@/components/StickyActionsHeader/StickyActionsHeader";
import { CONTENT_WIDTH, EntityStatus } from "@/enums";
import { getFlatFormFields } from "@/helpers";
import { antdWrapperColType } from "@/propTypes";
import { Form } from "antd";
import {
  any,
  arrayOf,
  bool,
  element,
  func,
  node,
  shape,
  string,
} from "prop-types";
import { cloneElement, Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import DynamicFormField from "./DynamicFormField";
import * as S from "./styled";
import { CategoryTypeHeaderItem } from "./styled";

const DEFAULT_WRAPPER_COL = {
  xs: CONTENT_WIDTH.max,
  sm: CONTENT_WIDTH.max,
  md: CONTENT_WIDTH.threeQuarters,
  lg: CONTENT_WIDTH.half,
  xl: CONTENT_WIDTH.threeEighths,
};

export const DynamicFormContent = ({
  onFinish,
  wrapperCol = DEFAULT_WRAPPER_COL,
  layout = "vertical",
  formDisabled,
  lockEntireForm,
  canChangeFields,
  leftActions,
  rightActions,
  placeholderId,
  extraInputs,
  extraCategories,
  formHeader,
  independentlyEditableFields,
}) => {
  const intl = useIntl();
  const {
    formConfig,
    handleValuesChange,
    formRef,
    showFormFieldByDependentProperty,
    initialValues,
  } = useDynamicFormContext();
  const submitProvider = useProjectIntakeSubmitContext();
  const [flatFormFields, setFlatFormFields] = useState([]);
  const [errorMessages, setErrorMessages] = useState();

  useEffect(() => {
    if (formConfig) {
      setFlatFormFields(
        getFlatFormFields(formConfig).filter(
          ({ property: { status } }) => status === EntityStatus.Active
        )
      );
    }
  }, [formConfig]);

  const setRef = (item) => {
    formRef.current = item;
    if (submitProvider?.setFormRef) {
      submitProvider.setFormRef(item);
    }
  };

  const handleOnFinish = (values) => {
    // add data back in from extra inputs post validation
    extraInputs?.forEach((input) => {
      const extraValue = formRef.current.getFieldValue(input.propertyName);
      values = { ...values, [input.propertyName]: extraValue };
    });

    if (onFinish) {
      onFinish(values, formRef);
    }
  };

  const fieldIsVisible = ({
    property: { dependsOnPropertyId: id, dependsOnPropertyValues: values },
  }) => {
    if (!id) return true;

    return showFormFieldByDependentProperty(id, values);
  };

  const getCategoryFields = (category) =>
    flatFormFields.filter(
      (formField) =>
        formField.category.label.id.includes(category.id) &&
        fieldIsVisible(formField)
    );

  const categories = flatFormFields
    .filter(
      (value, index, self) =>
        index ===
        self.findIndex(
          ({ category }) => category.label.id === value.category.label.id
        )
    )
    .map(({ category }) => category);

  const hardcodedCategories = () => {
    if (!extraCategories?.length) return null;

    return extraCategories.map((extraCategory) => (
      <S.DynamicCardWrapper
        xxl={CONTENT_WIDTH.oneQuarter}
        xl={CONTENT_WIDTH.oneThird}
        lg={CONTENT_WIDTH.half}
        xs={CONTENT_WIDTH.max}
        key={extraCategory.title}
      >
        <S.DynamicCard
          size="small"
          title={extraCategory.title}
          $required={extraCategory.required}
        >
          {cloneElement(extraCategory.inputComponent, {
            formRef: formRef.current,
            ...extraCategory.inputComponent.rest,
          })}
        </S.DynamicCard>
      </S.DynamicCardWrapper>
    ));
  };

  const disableFormField = (formField) => {
    const cantEditField =
      !canChangeFields || formField.property.lockOnSubmission;
    const isNotIndependentlyEditableField =
      independentlyEditableFields.includes(formField.property.name);

    return formDisabled && cantEditField && !isNotIndependentlyEditableField;
  };

  const createCategoryCards = ({
    label,
    label: { id, fallback },
    isRequired,
  }) => {
    const categoryFields = getCategoryFields(label);

    if (!categoryFields?.length) return null;
    return (
      <S.DynamicCardWrapper
        xxl={CONTENT_WIDTH.oneQuarter}
        xl={CONTENT_WIDTH.oneThird}
        lg={CONTENT_WIDTH.half}
        xs={CONTENT_WIDTH.max}
        key={id}
      >
        <S.DynamicCard
          size="small"
          title={intl.formatMessage({
            id,
            defaultMessage: fallback,
          })}
          $required={isRequired}
        >
          {categoryFields.map((formField) => (
            <Fragment key={formField.property.id}>
              <DynamicFormField
                formField={formField}
                formDisabled={lockEntireForm || disableFormField(formField)}
                placeholderId={placeholderId}
              />
            </Fragment>
          ))}
        </S.DynamicCard>
      </S.DynamicCardWrapper>
    );
  };

  const requiredFieldsForm = categories
    .filter(({ isRequired }) => isRequired === true)
    .map(createCategoryCards);

  const additionalInformationForm = categories
    .filter(({ isRequired }) => isRequired === false)
    .map(createCategoryCards);

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields.length) {
      setErrorMessages([
        intl.formatMessage({ id: "general.fieldRequiredMessage" }),
      ]);
    }
  };

  const setFormValuesFromExtraElement = (label, value) => {
    formRef.current.setFieldsValue({ [label]: value });
  };

  return (
    <Form
      wrapperCol={wrapperCol}
      layout={layout}
      onFinish={handleOnFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={handleValuesChange}
      initialValues={initialValues}
      preserve
      scrollToFirstError
      ref={setRef}
    >
      {rightActions && (
        <StickyActionsHeader
          leftChildren={leftActions}
          rightChildren={rightActions}
        ></StickyActionsHeader>
      )}
      <S.DynamicRow justify="start">
        {formHeader}
        <CategoryTypeHeaderItem>
          {intl.formatMessage({
            id: "projectIntake.requiredFields",
          })}
        </CategoryTypeHeaderItem>
        {formRef.current && hardcodedCategories()}
        {requiredFieldsForm}
      </S.DynamicRow>
      <S.DynamicRow justify="start">
        <CategoryTypeHeaderItem>
          {intl.formatMessage({
            id: "projectIntake.additionalInformation",
          })}
        </CategoryTypeHeaderItem>
        {additionalInformationForm}
        {formRef.current &&
          extraInputs?.map((item) => (
            <S.DynamicCardWrapper
              xxl={CONTENT_WIDTH.oneQuarter}
              xl={CONTENT_WIDTH.oneThird}
              lg={CONTENT_WIDTH.half}
              xs={CONTENT_WIDTH.max}
              key={`extra-input-${item.propertyName}`}
            >
              <S.DynamicCard title={item.title} size="small">
                {cloneElement(item.inputComponent, {
                  setFormValue: setFormValuesFromExtraElement,
                  initialValuesFromForm: item.initialValues,
                  ...item.rest,
                })}
              </S.DynamicCard>
            </S.DynamicCardWrapper>
          ))}
      </S.DynamicRow>
      {errorMessages && <ValidationMessages errorMessages={errorMessages} />}
    </Form>
  );
};

DynamicFormContent.propTypes = {
  onFinish: func,
  wrapperCol: antdWrapperColType,
  layout: string,
  isSubmitting: bool,
  formDisabled: bool,
  lockEntireForm: bool,
  canChangeFields: bool,
  leftActions: node,
  rightActions: node,
  placeholderId: string,
  extraInputs: arrayOf(
    shape({
      propertyName: string,
      title: string,
      inputComponent: element,
      initialValues: any,
    })
  ),
  extraCategories: arrayOf(
    shape({
      title: string,
      inputComponent: element,
      required: bool,
    })
  ),
  formHeader: element,
  independentlyEditableFields: arrayOf(string),
};
